import React from "react";
import styled from "styled-components";

import { useAppTheme } from "../../../app/providers/ThemeProvider";
import {
  THEME_ACCENT_OPTIONS,
  type ThemeAccentId,
} from "../../../theme/themeAccentPalettes";
import { Md3Button } from "../Md3Button";
import { fontSizes, lineHeights } from "../../styles/typography";

const Panel = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.md3.shape.extraLarge}px;
  padding: ${({ theme }) => theme.spacing.lg}px;
  box-shadow: ${({ theme }) => theme.shadows.md3Level1};
`;

const Title = styled.h3`
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textTitle};
  margin: 0 0 16px;
`;

const Subtitle = styled.h4`
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textTitle};
  margin: 0 0 12px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const AccentSwatch = styled.button<{ $color: string; $active?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 3px solid
    ${({ $active, theme }) => ($active ? theme.colors.text : "transparent")};
  background: ${({ $color }) => $color};
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const accentPreview: Record<ThemeAccentId, string> = {
  purple: "#4614BB",
  pink: "#DB2777",
  blue: "#4A2BB8",
  green: "#059669",
  orange: "#D97706",
};

// eslint-disable-next-line import/prefer-default-export
export function ThemeSettingsPanel() {
  const {
    isDarkMode,
    isHighContrast,
    accentId,
    toggleDarkMode,
    toggleHighContrast,
    setAccentId,
  } = useAppTheme();

  return (
    <Panel aria-label="Personalização do tema">
      <Title>Personalização</Title>

      <Row>
        <Md3Button
          title={isDarkMode ? "Modo claro" : "Modo escuro"}
          variant={isDarkMode ? "tonal" : "outlined"}
          onClick={toggleDarkMode}
        />
        <Md3Button
          title={isHighContrast ? "Contraste normal" : "Alto contraste"}
          variant={isHighContrast ? "tonal" : "outlined"}
          color="secondary"
          onClick={toggleHighContrast}
        />
      </Row>

      <Subtitle>Cor de destaque</Subtitle>
      <Row role="group" aria-label="Cor de destaque">
        {THEME_ACCENT_OPTIONS.map(({ id, label }) => (
          <AccentSwatch
            key={id}
            type="button"
            $color={accentPreview[id]}
            $active={accentId === id}
            aria-label={label}
            aria-pressed={accentId === id}
            onClick={() => setAccentId(id)}
          />
        ))}
      </Row>
    </Panel>
  );
}
